import { DRAWING_COLORS, LINE_STYLES, DIMENSION } from './constants';
import { projectTo2D } from './AnnotationLayer';
import type { DimensionSpec } from '@shared/types';
import type { ViewBox } from '@/utils/svgPathParser';

const C = DRAWING_COLORS;
const JP_FONT = "'Noto Sans JP', sans-serif";

type SmartDimensionProps = {
  dimension: DimensionSpec;
  viewType: string;
  viewBox: ViewBox;
  /** Stacking index — 0 = closest to geometry, 1 = next outward, etc. */
  stackIndex?: number;
};

/**
 * Map a projected 2D coordinate into viewBox-relative space.
 */
function toViewBoxCoords(
  projected: { x: number; y: number },
  viewBox: ViewBox,
): { x: number; y: number } {
  return {
    x: Math.max(viewBox.minX, Math.min(viewBox.minX + viewBox.width, projected.x)),
    y: Math.max(viewBox.minY, Math.min(viewBox.minY + viewBox.height, projected.y)),
  };
}

export function SmartDimension({ dimension, viewType, viewBox, stackIndex = 0 }: SmartDimensionProps) {
  if (dimension.view !== viewType) return null;

  const from2d = toViewBoxCoords(projectTo2D(dimension.from, viewType), viewBox);
  const to2d = toViewBoxCoords(projectTo2D(dimension.to, viewType), viewBox);

  // Format value text
  let valueText = '';
  switch (dimension.type) {
    case 'diameter':
      valueText = `⌀${dimension.value}`;
      break;
    case 'radius':
      valueText = `R${dimension.value}`;
      break;
    default:
      valueText = dimension.isReference ? `[${dimension.value}]` : `${dimension.value}`;
  }
  if (dimension.tolerance) {
    valueText += ` ${dimension.tolerance}`;
  }

  // Chain dimensions
  if (dimension.type === 'chain' && dimension.chainSegments) {
    return (
      <ChainDimension
        from={from2d}
        to={to2d}
        segments={dimension.chainSegments}
        totalValue={dimension.value}
        isReference={dimension.isReference}
        viewBox={viewBox}
        stackIndex={stackIndex}
      />
    );
  }

  // Determine orientation
  const dx = Math.abs(to2d.x - from2d.x);
  const dy = Math.abs(to2d.y - from2d.y);
  const isHorizontal = dx > dy;

  if (isHorizontal) {
    return (
      <HorizontalDimension
        from={from2d} to={to2d}
        text={valueText}
        viewBox={viewBox}
        stackIndex={stackIndex}
      />
    );
  }

  return (
    <VerticalDimension
      from={from2d} to={to2d}
      text={valueText}
      viewBox={viewBox}
      stackIndex={stackIndex}
    />
  );
}

/**
 * Render a group of SmartDimensions for a view, automatically computing
 * stacking indices so overlapping dimensions fan outward.
 */
export function SmartDimensionGroup({
  dimensions,
  viewType,
  viewBox,
}: {
  dimensions: DimensionSpec[];
  viewType: string;
  viewBox: ViewBox;
}) {
  // Filter to this view
  const viewDims = dimensions.filter((d) => d.view === viewType);

  // Separate by orientation to compute stacking
  const horizontal: (DimensionSpec & { _idx: number })[] = [];
  const vertical: (DimensionSpec & { _idx: number })[] = [];

  viewDims.forEach((d, i) => {
    if (d.type === 'chain') {
      horizontal.push({ ...d, _idx: i });
      return;
    }
    const from2d = projectTo2D(d.from, viewType);
    const to2d = projectTo2D(d.to, viewType);
    const dx = Math.abs(to2d.x - from2d.x);
    const dy = Math.abs(to2d.y - from2d.y);
    if (dx > dy) {
      horizontal.push({ ...d, _idx: i });
    } else {
      vertical.push({ ...d, _idx: i });
    }
  });

  // Sort by span length (shortest first = closest to geometry)
  const sortBySpan = (a: DimensionSpec, b: DimensionSpec) => {
    const spanA = Math.abs(a.value);
    const spanB = Math.abs(b.value);
    return spanA - spanB;
  };
  horizontal.sort(sortBySpan);
  vertical.sort(sortBySpan);

  return (
    <g className="smart-dimensions">
      {horizontal.map((d, stackIdx) => (
        <SmartDimension
          key={`h-${d._idx}`}
          dimension={d}
          viewType={viewType}
          viewBox={viewBox}
          stackIndex={stackIdx}
        />
      ))}
      {vertical.map((d, stackIdx) => (
        <SmartDimension
          key={`v-${d._idx}`}
          dimension={d}
          viewType={viewType}
          viewBox={viewBox}
          stackIndex={stackIdx}
        />
      ))}
    </g>
  );
}

function HorizontalDimension({
  from, to, text, viewBox, stackIndex,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  text: string;
  viewBox: ViewBox;
  stackIndex: number;
}) {
  const baseOffset = DIMENSION.offsetFromGeometry;
  const offset = baseOffset + stackIndex * DIMENSION.stackSpacing;
  const lineY = viewBox.minY + viewBox.height + offset;
  const x1 = Math.min(from.x, to.x);
  const x2 = Math.max(from.x, to.x);
  const midX = (x1 + x2) / 2;

  return (
    <g className="smart-dimension horizontal">
      {/* Extension lines */}
      <line x1={x1} y1={from.y + DIMENSION.extensionGap} x2={x1} y2={lineY + DIMENSION.extensionOvershoot}
        stroke={C.dimension} strokeWidth={0.2} />
      <line x1={x2} y1={to.y + DIMENSION.extensionGap} x2={x2} y2={lineY + DIMENSION.extensionOvershoot}
        stroke={C.dimension} strokeWidth={0.2} />
      {/* Dimension line with arrows */}
      <line x1={x1} y1={lineY} x2={x2} y2={lineY}
        stroke={C.dimension} strokeWidth={LINE_STYLES.dimension.strokeWidth}
        markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)" />
      {/* Value text */}
      <text x={midX} y={lineY - 1.5}
        textAnchor="middle" dominantBaseline="auto"
        fontSize={DIMENSION.fontSize} fontFamily={JP_FONT} fill={C.dimension} fontWeight="500">
        {text}
      </text>
    </g>
  );
}

function VerticalDimension({
  from, to, text, viewBox, stackIndex,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  text: string;
  viewBox: ViewBox;
  stackIndex: number;
}) {
  const baseOffset = DIMENSION.offsetFromGeometry;
  const offset = baseOffset + stackIndex * DIMENSION.stackSpacing;
  const lineX = viewBox.minX + viewBox.width + offset;
  const y1 = Math.min(from.y, to.y);
  const y2 = Math.max(from.y, to.y);
  const midY = (y1 + y2) / 2;

  return (
    <g className="smart-dimension vertical">
      {/* Extension lines */}
      <line x1={from.x + DIMENSION.extensionGap} y1={y1} x2={lineX + DIMENSION.extensionOvershoot} y2={y1}
        stroke={C.dimension} strokeWidth={0.2} />
      <line x1={to.x + DIMENSION.extensionGap} y1={y2} x2={lineX + DIMENSION.extensionOvershoot} y2={y2}
        stroke={C.dimension} strokeWidth={0.2} />
      {/* Dimension line with arrows */}
      <line x1={lineX} y1={y1} x2={lineX} y2={y2}
        stroke={C.dimension} strokeWidth={LINE_STYLES.dimension.strokeWidth}
        markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)" />
      {/* Value text (rotated) */}
      <text x={lineX} y={midY}
        textAnchor="middle" dominantBaseline="central"
        fontSize={DIMENSION.fontSize} fontFamily={JP_FONT} fill={C.dimension} fontWeight="500"
        transform={`rotate(-90, ${lineX}, ${midY})`}>
        {text}
      </text>
    </g>
  );
}

function ChainDimension({
  from, to, segments, totalValue, isReference, viewBox, stackIndex,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  segments: { value: number; tolerance?: string }[];
  totalValue: number;
  isReference?: boolean;
  viewBox: ViewBox;
  stackIndex: number;
}) {
  const baseOffset = DIMENSION.offsetFromGeometry;
  const offset = baseOffset + stackIndex * DIMENSION.stackSpacing;
  const lineY = viewBox.minY + viewBox.height + offset;
  const totalLineY = lineY + DIMENSION.stackSpacing;
  const x1 = Math.min(from.x, to.x);
  const x2 = Math.max(from.x, to.x);
  const totalWidth = x2 - x1;

  // Distribute segments proportionally
  let currentX = x1;
  const segmentPositions = segments.map((seg) => {
    const ratio = seg.value / totalValue;
    const segW = totalWidth * ratio;
    const pos = { x: currentX, w: segW };
    currentX += segW;
    return pos;
  });

  return (
    <g className="smart-dimension chain">
      {/* Extension lines from geometry to outermost dimension */}
      {segmentPositions.map((pos, i) => (
        <line key={`ext-${i}`}
          x1={pos.x} y1={viewBox.minY + viewBox.height + DIMENSION.extensionGap}
          x2={pos.x} y2={totalLineY + DIMENSION.extensionOvershoot}
          stroke={C.dimension} strokeWidth={0.15} />
      ))}
      {/* Last extension line */}
      <line
        x1={x2} y1={viewBox.minY + viewBox.height + DIMENSION.extensionGap}
        x2={x2} y2={totalLineY + DIMENSION.extensionOvershoot}
        stroke={C.dimension} strokeWidth={0.15} />

      {/* Individual segments */}
      {segmentPositions.map((pos, i) => {
        const seg = segments[i];
        const midX = pos.x + pos.w / 2;
        let text = `${seg.value}`;
        if (seg.tolerance) text += ` ${seg.tolerance}`;

        return (
          <g key={i}>
            <line x1={pos.x} y1={lineY} x2={pos.x + pos.w} y2={lineY}
              stroke={C.dimension} strokeWidth={LINE_STYLES.dimension.strokeWidth}
              markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)" />
            <text x={midX} y={lineY - 1.5}
              textAnchor="middle" dominantBaseline="auto"
              fontSize={DIMENSION.fontSize * 0.85} fontFamily={JP_FONT} fill={C.dimension}>
              {text}
            </text>
          </g>
        );
      })}

      {/* Total reference dimension */}
      <line x1={x1} y1={totalLineY} x2={x2} y2={totalLineY}
        stroke={C.dimension} strokeWidth={LINE_STYLES.dimension.strokeWidth}
        markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)" />
      <text x={(x1 + x2) / 2} y={totalLineY - 1.5}
        textAnchor="middle" dominantBaseline="auto"
        fontSize={DIMENSION.fontSize} fontFamily={JP_FONT} fill={C.dimension} fontWeight="500">
        {isReference ? `[${totalValue}]` : `${totalValue}`}
      </text>
    </g>
  );
}
